import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { FONT } from './landingTokens';

/**
 * Intro that plays on every full page load, including reloads. Dark, like the
 * other DecisionOpt product pages, and deliberately minimal. One gesture:
 * a single route leaves the depot, glides through three stops (each turns
 * "delivered" as the vehicle reaches it), then runs on as the underline that
 * reveals the ExPlan wordmark.
 *
 * Everything runs on one SVG (SMIL) clock so the line, the vehicle, the stops
 * and the wordmark reveal stay in sync. The stop times below come from the
 * route's arc length: each stop sits at a known fraction of the path, and the
 * vehicle moves at constant speed.
 *
 * It does not replay on in-app navigation (for example /login and back to /),
 * only when the page itself loads. Never shown when the visitor prefers reduced
 * motion, or when the page loads in a background tab where nobody would see it.
 * Any click or key skips it. It is aria-hidden: screen readers go straight to
 * the page behind it.
 */

// Module state lives exactly as long as the page: a reload resets it, a route
// change inside the app does not.
let playedThisPageLoad = false;

// Every timing below is written at 1x and divided by SPEED. Raise it to make the
// whole intro faster, lower it to slow it down.
const SPEED = 1.3;
/** Scaled seconds as an SVG/CSS time string. */
const sec = (seconds: number) => `${(seconds / SPEED).toFixed(3)}s`;

const START = 0.3; // seconds (at 1x) before the vehicle leaves the depot
const DRIVE = 1.8; // seconds (at 1x) for the whole route, underline included
const FADE_OUT_AT = 2.95;
const FADE_OUT = 0.5;
const TOTAL_MS = Math.round(((FADE_OUT_AT + FADE_OUT) / SPEED) * 1000);
const SKIP_FADE_MS = 260;

const ROUTE =
  'M40 190 C 95 190, 110 110, 170 110 C 230 110, 240 200, 300 200 C 360 200, 370 120, 430 120 C 495 120, 480 212, 545 212 L 915 212';

// Fractions of the route's length where each stop sits, and where the
// underline under the wordmark begins.
const STOPS: { x: number; y: number; at: number }[] = [
  { x: 170, y: 110, at: 0.1567 },
  { x: 300, y: 200, at: 0.3201 },
  { x: 430, y: 120, at: 0.4772 },
];
const UNDERLINE_AT = 0.6327;

// Dark, like the splash screens on the sibling DecisionOpt product pages.
const DARK = {
  bg: '#0f172a',
  grid: 'rgba(91,141,239,0.07)',
  route: '#5b8def',
  delivered: '#8fb2ff',
  text: '#ffffff',
  tagline: '#8fa3c7',
  hint: '#56688f',
};

const t = (fraction: number) => sec(START + DRIVE * fraction);

const shouldShow = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  if (document.visibilityState === 'hidden') return false;
  return !playedThisPageLoad;
};

const LandingSplash: React.FC = () => {
  const [visible, setVisible] = useState<boolean>(shouldShow);
  const [leaving, setLeaving] = useState(false);

  const skip = useCallback(() => setLeaving(true), []);

  useEffect(() => {
    if (!visible) return undefined;
    playedThisPageLoad = true;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const done = window.setTimeout(() => setVisible(false), TOTAL_MS);
    window.addEventListener('keydown', skip);
    return () => {
      window.clearTimeout(done);
      window.removeEventListener('keydown', skip);
      document.body.style.overflow = previousOverflow;
    };
  }, [visible, skip]);

  useEffect(() => {
    if (!leaving) return undefined;
    const id = window.setTimeout(() => setVisible(false), SKIP_FADE_MS);
    return () => window.clearTimeout(id);
  }, [leaving]);

  if (!visible) return null;

  return (
    <Box
      aria-hidden="true"
      onClick={skip}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        bgcolor: DARK.bg,
        backgroundImage: [
          'radial-gradient(55% 45% at 50% 48%, rgba(91,141,239,0.12) 0%, rgba(91,141,239,0) 70%)',
          `linear-gradient(${DARK.grid} 1px, transparent 1px)`,
          `linear-gradient(90deg, ${DARK.grid} 1px, transparent 1px)`,
        ].join(', '),
        backgroundSize: '100% 100%, 60px 60px, 60px 60px',
        backgroundPosition: 'center, center, center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        opacity: leaving ? 0 : 1,
        transition: `opacity ${SKIP_FADE_MS}ms ease`,
        animation: `splashOut ${sec(FADE_OUT)} ease ${sec(FADE_OUT_AT)} forwards`,
        '@keyframes splashOut': { to: { opacity: 0 } },
        '@keyframes splashFadeUp': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        component="svg"
        viewBox="-14 40 960 220"
        sx={{ width: 'min(88vw, 860px)', height: 'auto', overflow: 'visible' }}
      >
        <defs>
          <filter id="explanSplashGlow" x="-20%" y="-60%" width="140%" height="220%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <clipPath id="explanSplashReveal">
            <rect x="540" y="60" width="0" height="200">
              <animate
                attributeName="width"
                from="0"
                to="385"
                begin={t(UNDERLINE_AT)}
                dur={sec(DRIVE * (1 - UNDERLINE_AT))}
                fill="freeze"
              />
            </rect>
          </clipPath>
        </defs>

        {/* Faint ghost of the whole route, so the eye knows where it is going. */}
        <path d={ROUTE} fill="none" stroke={DARK.route} strokeOpacity="0.14" strokeWidth="3" strokeLinecap="round" />

        <path
          d={ROUTE}
          fill="none"
          stroke={DARK.route}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={1}
          strokeDasharray="1"
          strokeDashoffset="1"
          filter="url(#explanSplashGlow)"
        >
          <animate attributeName="stroke-dashoffset" from="1" to="0" begin={sec(START)} dur={sec(DRIVE)} fill="freeze" />
        </path>

        {/* Depot */}
        <g opacity="0">
          <animate attributeName="opacity" from="0" to="1" begin={sec(0.05)} dur={sec(0.25)} fill="freeze" />
          <circle cx="40" cy="190" r="22" fill={DARK.route} fillOpacity="0.18" />
          <rect x="30" y="180" width="20" height="20" rx="5" fill={DARK.route} />
        </g>

        {STOPS.map(stop => (
          <circle key={stop.x} cx={stop.x} cy={stop.y} r="0" fill={DARK.bg} stroke={DARK.route} strokeWidth="3">
            <animate
              attributeName="r"
              values="0;9;7"
              keyTimes="0;0.6;1"
              begin={t(Math.max(0, stop.at - 0.05))}
              dur={sec(0.3)}
              fill="freeze"
            />
            <set attributeName="fill" to={DARK.delivered} begin={t(stop.at)} />
          </circle>
        ))}

        <text
          x="548"
          y="186"
          clipPath="url(#explanSplashReveal)"
          fontFamily={FONT}
          fontSize="112"
          fontWeight="800"
          letterSpacing="-3.5"
          fill={DARK.text}
        >
          ExPlan
        </text>

        {/* The vehicle rides the tip of the line. */}
        <g opacity="0">
          <set attributeName="opacity" to="1" begin={sec(START)} />
          <circle r="16" fill={DARK.route} fillOpacity="0.25" />
          <circle r="7.5" fill="#ffffff" stroke={DARK.route} strokeWidth="3.5" />
          <animateMotion path={ROUTE} begin={sec(START)} dur={sec(DRIVE)} fill="freeze" />
        </g>
      </Box>

      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: { xs: 11.5, sm: 13 },
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: DARK.tagline,
          mt: { xs: 2, sm: 3 },
          textAlign: 'center',
          px: 2,
          opacity: 0,
          animation: `splashFadeUp ${sec(0.45)} cubic-bezier(.16,1,.3,1) ${sec(START + DRIVE)} forwards`,
        }}
      >
        Last-Mile Route Planning &amp; Tracking · by{' '}
        <Box component="span" sx={{ color: DARK.delivered }}>
          DecisionOpt
        </Box>
      </Typography>

      <Typography
        sx={{
          position: 'absolute',
          bottom: { xs: 22, sm: 28 },
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: FONT,
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: DARK.hint,
          opacity: 0,
          animation: `splashFadeUp ${sec(0.4)} ease ${sec(0.8)} forwards`,
        }}
      >
        Click or press any key to skip
      </Typography>
    </Box>
  );
};

export default LandingSplash;
